import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

const FS_TOKENS = [
  'fs-10',
  'fs-11',
  'fs-12',
  'fs-13',
  'fs-14',
  'fs-15',
  'fs-16',
  'fs-18',
  'fs-20',
  'fs-22',
  'fs-24',
  'fs-28',
  'fs-30',
  'fs-32',
  'fs-36',
  'fs-46',
  'fs-48',
  'fs-60',
  'fs-72',
  'chip',
  'pill',
  'nav',
  'reply',
] as const;

const SHADOW_TOKENS = [
  'e1',
  'e2',
  'e3',
  'e-float',
  'e-first-hover',
  'ring',
] as const;

type SafeBorderGroup =
  | 'safe-border-t'
  | 'safe-border-r'
  | 'safe-border-b'
  | 'safe-border-l';

const twMerge = extendTailwindMerge<SafeBorderGroup>({
  extend: {
    classGroups: {
      'font-size': [{ text: [...FS_TOKENS] }],
      shadow: [{ shadow: [...SHADOW_TOKENS] }],
      'inset-shadow': [{ 'inset-shadow': ['rim'] }],
      rounded: [{ rounded: ['pill'] }],
      'safe-border-t': ['border-safe-t'],
      'safe-border-r': ['border-safe-r'],
      'safe-border-b': ['border-safe-b'],
      'safe-border-l': ['border-safe-l'],
    },
  },
});

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export const toFiniteNumber = (
  value: number | string | null | undefined,
): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const toNumber = (value: number | string | null | undefined): number =>
  toFiniteNumber(value) ?? 0;

// The same in-app path, hash and other params, minus `name`.
export const withoutSearchParam = (href: string, name: string): string => {
  const url = new URL(href, window.location.origin);
  url.searchParams.delete(name);
  return `${url.pathname}${url.search}${url.hash}`;
};

export const truncate = (text: string, max = 60): string =>
  text.length <= max ? text : `${text.slice(0, max).trimEnd()}…`;

export type NonEmptyArray<T> = [T, ...T[]];

export const toNonEmptyArray = <T>(items: readonly T[]): NonEmptyArray<T> => {
  if (items.length === 0) {
    throw new Error('Expected a non-empty array');
  }
  return items as unknown as NonEmptyArray<T>;
};
